<template>
  <div class="org">
    <h1 class="org-h1">
      My Organization
    </h1>
    <p class="org-lead">
      The locations and people that belong to your organization.
    </p>

    <div class="org-stack">
      <ds-card
        eyebrow="Organization"
        :title="organization ? organization.name : 'No organization'"
      >
        <template #actions>
          <ds-badge variant="wine">
            {{ locationCount }} {{ locationCount === 1 ? 'location' : 'locations' }}
          </ds-badge>
        </template>
      </ds-card>

      <ds-tabs
        ref="tabs"
        v-model="activeTab"
        :tabs="tabs"
        id-prefix="org"
      />

      <ds-card
        :id="`org-panel-${activeTab}`"
        flush
        role="tabpanel"
        :aria-labelledby="`org-tab-${activeTab}`"
        tabindex="0"
      >
        <ds-table
          v-if="activeTab === 'locations'"
          :fields="locationFields"
          :items="locations"
          empty-text="No locations in this organization."
        >
          <template #cell(city)="{ item }">
            <template v-if="cityName(item)">
              {{ cityName(item) }}
            </template>
            <template v-else>
              <span aria-hidden="true">-</span><span class="sr-only">Not set</span>
            </template>
          </template>
        </ds-table>

        <!-- Mounted only while selected, so members fetch on open (b-tab `lazy`). -->
        <organization-members v-else-if="activeTab === 'members'" />
      </ds-card>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import {
  DsBadge, DsCard, DsTable, DsTabs,
} from '@/design-system/components'
import OrganizationMembers from '@/components/organization/OrganizationMembers.vue'

export default {
  name: 'Organization',
  components: {
    DsBadge,
    DsCard,
    DsTable,
    DsTabs,
    OrganizationMembers,
  },
  data() {
    return {
      activeTab: 'locations',
      locationFields: [
        { key: 'name', label: 'Name' },
        { key: 'city', label: 'City' },
        { key: 'address', label: 'Address' },
        { key: 'email', label: 'Email' },
        { key: 'phone', label: 'Phone' },
      ],
    }
  },
  computed: {
    ...mapGetters({
      organization: 'authentication/organization',
      locations: 'authentication/locations',
      isAdmin: 'authentication/isAdmin',
      cities: 'cities/cities',
    }),
    // partner/me sends locations with only a cityId, so names come from the cities list
    // App.vue loads. Reactive, so the column fills in when that request lands.
    cityNames() {
      return new Map((this.cities || []).map(city => [city.id, city.name]))
    },
    locationCount() {
      return this.locations.length
    },
    // Admin-only Members tab appears once the partner (and isAdmin) resolves.
    tabs() {
      const tabs = [{ key: 'locations', label: 'Locations', count: this.locationCount }]
      if (this.isAdmin) tabs.push({ key: 'members', label: 'Members' })
      return tabs
    },
  },
  methods: {
    // null when unknown, so the template can render the "Not set" pairing.
    cityName(location) {
      return location.cityName
        || (location.city && location.city.name)
        || this.cityNames.get(location.cityId)
        || null
    },
  },
}
</script>

<style scoped lang="scss">
@import '~@/design-system/mixins';

.org {
  max-width: 1140px;
  margin: 0 auto;
  color: $c-text;
}

.org-h1 {
  @include ds-page-title;
}

.org-lead {
  @include ds-lead;
}

.org-stack {
  @include ds-stack;

  align-items: stretch;
}

// Don't stretch the segmented control to the stack's width.
.ds-tabs {
  align-self: flex-start;
}
</style>
